const MAX_REFERENCE_CHAIN = 16;
/** Fusionne un thème sur son parent (`extends`) : l'enfant gagne, jeton par jeton. */
function layers(theme, parent, mode) {
    const semanticOf = (set) => ({
        ...(set?.semantic ?? {}),
        ...(mode === 'default' ? {} : (set?.modes?.[mode] ?? {})),
    });
    return {
        primitives: { ...(parent?.primitives ?? {}), ...(theme.primitives ?? {}) },
        semantic: { ...semanticOf(parent), ...semanticOf(theme) },
    };
}
function resolveToken(path, { primitives, semantic }) {
    const seen = [];
    let current = path;
    for (let step = 0; step <= MAX_REFERENCE_CHAIN; step += 1) {
        if (seen.includes(current))
            return { ok: false, code: 'THEME_REFERENCE_CYCLE', message: `Cycle de jetons : ${[...seen, current].join(' → ')}.` };
        seen.push(current);
        if (Object.hasOwn(semantic, current)) {
            const entry = semantic[current];
            if ('value' in entry)
                return { ok: true, value: entry.value };
            current = entry.ref;
            continue;
        }
        if (Object.hasOwn(primitives, current))
            return { ok: true, value: primitives[current] };
        return { ok: false, code: 'THEME_REFERENCE_BROKEN', message: `Le jeton « ${current} » n'existe pas.` };
    }
    return { ok: false, code: 'THEME_REFERENCE_TOO_LONG', message: `Plus de ${MAX_REFERENCE_CHAIN} renvois.` };
}
export function validateThemeTokenSet(theme, parent) {
    const findings = [];
    const modes = ['default', ...Object.keys(theme.modes ?? {})];
    for (const mode of modes) {
        const resolved = layers(theme, parent, mode);
        const own = mode === 'default' ? theme.semantic : (theme.modes?.[mode] ?? {});
        for (const path of Object.keys(own)) {
            const result = resolveToken(path, resolved);
            const at = mode === 'default' ? `/semantic/${path}` : `/modes/${mode}/${path}`;
            if (!result.ok)
                findings.push({ path: at, code: result.code, message: result.message });
        }
    }
    // Un mode ne crée pas de jeton : il surcharge un jeton sémantique existant.
    for (const [mode, overrides] of Object.entries(theme.modes ?? {})) {
        for (const path of Object.keys(overrides)) {
            if (!Object.hasOwn(theme.semantic, path) && !Object.hasOwn(parent?.semantic ?? {}, path)) {
                findings.push({ path: `/modes/${mode}/${path}`, code: 'THEME_MODE_NEW_TOKEN', message: `Le mode « ${mode} » surcharge « ${path} », absent de la couche sémantique.` });
            }
        }
    }
    // La couche composant ne lit que des jetons sémantiques.
    const semantic = new Set([...Object.keys(parent?.semantic ?? {}), ...Object.keys(theme.semantic)]);
    for (const [component, styles] of Object.entries(theme.components ?? {})) {
        for (const [property, token] of Object.entries(styles)) {
            if (!semantic.has(token))
                findings.push({ path: `/components/${component}/${property}`, code: 'THEME_COMPONENT_NOT_SEMANTIC', message: `« ${token} » n'est pas un jeton sémantique.` });
        }
    }
    return findings;
}
/**
 * Valeurs résolues des jetons sémantiques pour un mode : `{ "color.text.primary": "#111827" }`.
 * Un jeton qui ne se résout pas est absent (la validation l'a refusé avant publication).
 */
export function resolveTheme(theme, mode = 'default', parent) {
    const resolved = layers(theme, parent, mode);
    const out = {};
    for (const path of Object.keys(resolved.semantic)) {
        const result = resolveToken(path, resolved);
        if (result.ok)
            out[path] = result.value;
    }
    return out;
}
/** Portée `$theme` : les chemins pointés deviennent un objet imbriqué, lisible par l'évaluateur. */
export function themeScope(tokens) {
    const root = {};
    for (const [path, value] of Object.entries(tokens)) {
        const segments = path.split('.');
        let node = root;
        segments.forEach((segment, index) => {
            if (index === segments.length - 1)
                node[segment] = value;
            else {
                const next = node[segment];
                if (typeof next !== 'object' || next === null)
                    node[segment] = {};
                node = node[segment];
            }
        });
    }
    return root;
}
//# sourceMappingURL=theme.js.map